import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { joinBy } from '@pbk/utils';
import { addMonths, subMonths } from 'date-fns';
import { useState } from 'react';
import { styled } from 'styletron-react';
import { sizes } from '../../../../const/sizes';
import { getSpacing } from '../../../../const/spacing';
import { zIndexes } from '../../../../const/zIndexes';
import { Button } from '../../../Button/Button';
import { HorizontalDivider } from '../../../HorizontalDivider/HorizontalDivider';
import { FlexContainer } from '../../../layout/FlexContainer';
import { CalendarDays } from './CalendarDays';
import { CalendarHeader } from './CalendarHeader';
import { CalendarShortWeekDays } from './CalendarShortWeekDays';
import { MONTH_STEP_NUMBER } from './const';
import { getCalendarDates } from './helpers/getCalendarDates';
import { getCanNavigateNextMonth, getCanNavigatePreviousMonth, } from './helpers/getCanNavigate';
import { getFormattedNominativeMonth } from './helpers/getFormattedNominativeMonth';
import { getInitialCalendarDate } from './helpers/getInitialCalendarDate';
import { useGetFormattedYearNumber } from './hooks/useGetFormattedYearNumber';
import { useGetShortWeekDays } from './hooks/useGetShortWeekDays';
const StyledCalendar = styled('div', {
    padding: getSpacing('1'),
    zIndex: zIndexes.datepicker,
    position: 'relative',
    borderRadius: sizes.calendar.borderRadius,
});
export const Calendar = ({ onCloseCalendar, onSelectDate, selectedDate, minDate, maxDate, getIsDisabled, nominativeMonths, onClear, onToday, clearButtonText, todayButtonText, }) => {
    const [currentDate, setCurrentDate] = useState(() => getInitialCalendarDate({ selectedDate, minDate, maxDate }));
    const handleGoToNextMonth = () => {
        setCurrentDate((prevDate) => addMonths(prevDate, MONTH_STEP_NUMBER));
    };
    const handleGoToPreviousMonth = () => {
        setCurrentDate((prevDate) => subMonths(prevDate, MONTH_STEP_NUMBER));
    };
    const month = getFormattedNominativeMonth(currentDate, nominativeMonths);
    const year = useGetFormattedYearNumber(currentDate);
    const monthAndYearText = joinBy([month, year], ' ');
    const shortWeekDays = useGetShortWeekDays();
    const calendarDays = getCalendarDates({
        currentDate,
        selectedDate,
        minDate,
        maxDate,
        getIsDisabled,
    });
    const isCanNavigateToNextMonth = getCanNavigateNextMonth({
        currentDate,
        maxDate,
    });
    const isCanNavigateToPreviousMonth = getCanNavigatePreviousMonth({
        currentDate,
        minDate,
    });
    const hasFooter = !!onClear || !!onToday;
    const shouldShowClearButton = !!onClear && !!clearButtonText;
    const maybeClearButton = shouldShowClearButton ? (_jsx(Button, { color: 'grey', type: 'button', variant: 'underline', onClick: onClear, children: clearButtonText })) : null;
    const handleTodayClick = () => {
        const today = new Date();
        setCurrentDate(today);
        onToday?.();
    };
    const shouldShowTodayButton = !!onToday && !!todayButtonText;
    const maybeTodayButton = shouldShowTodayButton ? (_jsx(Button, { color: 'action', type: 'button', variant: 'underline', onClick: handleTodayClick, children: todayButtonText })) : null;
    return (_jsxs(StyledCalendar, { children: [_jsx(CalendarHeader, { headerText: monthAndYearText, isCanNavigateToNextMonth: isCanNavigateToNextMonth, isCanNavigateToPreviousMonth: isCanNavigateToPreviousMonth, onGoToNextMonth: handleGoToNextMonth, onGoToPreviousMonth: handleGoToPreviousMonth }), _jsx(CalendarShortWeekDays, { shortWeekDays: shortWeekDays }), _jsx(HorizontalDivider, {}), _jsx(CalendarDays, { calendarDays: calendarDays, onCloseCalendar: onCloseCalendar, onSelectDate: onSelectDate }), hasFooter && (_jsxs(FlexContainer, { horizontalAlign: 'spaceBetween', spacingTop: '2', gap: true, children: [maybeClearButton, maybeTodayButton] }))] }));
};
